import React from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import { faChrome, faFirefox } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import {
	faArrowToBottom,
	faBolt,
	faCheck,
	faCommentAltLines,
	faSignIn,
	faSpinnerThird,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compact, escapeRegExp, toLower } from "lodash";
import uuidv4 from "uuid/v4";

import { bookTrainingCall, ProductFamilies } from "~/billing/utils";
import confettiAnimationData from "~/common/Animations/confetti.json";
import companionIconDark from "~/common/AppInterstitial/companion-icon-dark.svg?url";
import companionIcon from "~/common/AppInterstitial/companion-icon.svg?url";
import notificationsIconDark from "~/common/AppInterstitial/notifications-icon-dark.svg?url";
import notificationsIcon from "~/common/AppInterstitial/notifications-icon.svg?url";
import trayIconDark from "~/common/AppInterstitial/tray-icon-dark.svg?url";
import trayIcon from "~/common/AppInterstitial/tray-icon.svg?url";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import ThemeContext from "~/common/ThemeContext";
import { ReactComponent as ConnectedIcon } from "~/legacy-assets/images/icons/connected.svg";
import { ReactComponent as MultiAssigneeIcon } from "~/legacy-assets/images/icons/multi-assignee-icon.svg";
import trainingImage from "~/legacy-assets/images/illustrations/PostUpgradeTraining.png";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	chromeStoreLink,
	desktopDownloadLink,
	emailGreylist,
	isChrome,
	isDesktop,
	isDesktopApp,
	isFirefox,
	isMacos,
	isTeams,
	isWindows,
	listFormat,
	mozillaStoreLink,
	nameFromEmailEntry,
	withQueryParams,
} from "~/lib/utils";
import { environment } from "~/models";
import { type Domain, type Invitation, type InviteeType, InviteInput } from "~/onboarding/InvitePage";
import { Button, toast } from "~/ui";
import Animation from "~/ui/Animation";
import { ButtonLink, ContactSupportLink, Modal } from "~/ui/legacyFellowIndex";

import desktopDark from "./illustration-desktop-dark.svg?url";
import desktopLight from "./illustration-desktop-light.svg?url";
import extensionDark from "./illustration-extension-dark.svg?url";
import extensionLight from "./illustration-extension-light.svg?url";

import type { PostUpgradeModalContactMutation as MutationType } from "~/__generated__/PostUpgradeModalContactMutation.graphql";
import type {
	PostUpgradeModalQuery as QueryType,
	PostUpgradeModalQuery$data as ResponseType,
} from "~/__generated__/PostUpgradeModalQuery.graphql";

import styles from "./css/PostUpgradeModal.module.css";

export const POST_UPGRADE_MODAL_SEEN_KEY = "PostUpgradeModal__seen";

type StepProps = {
	onNext: () => void;
};

type FeatureStepProps = StepProps & { user: ResponseType["viewer"]["user"] };
type InviteStepProps = StepProps & {
	user: ResponseType["viewer"]["user"];
	account: ResponseType["viewer"]["account"];
	invites: InviteeType[];
};
type IntegrateStepProps = StepProps & {
	appExtension: "extension" | "desktop";
};
type TrainingStepProps = StepProps & {
	user: ResponseType["viewer"]["user"];
};

const PostUpgradeModalQuery = graphql`
	query PostUpgradeModalQuery {
		contacts(first: 20, ranked: true, excludeUsers: true) {
			edges {
				node {
					email
					image
				}
			}
		}
		viewer {
			lastUsedDesktopApp
			lastUsedBrowserExtension
			questionAnswers
			user {
				id
				firstName
				lastName
				email
				isManager
				isAccountAdmin
				internalAttributes {
					isAccountCreator
					primaryConferenceCallProvider
				}
			}
			integrations {
				google {
					id
				}
			}
			account {
				allowInviting
				billingAccount {
					numBillableUsers
				}
				domains {
					domain
					aliases
				}
			}
		}
	}
`;

const PostUpgradeModalContactMutation = graphql`
	mutation PostUpgradeModalContactMutation($input: InviteUsersMutationInput!) {
		inviteUsers(input: $input) {
			invitees {
				errors {
					field
					message
				}
			}
		}
	}
`;

function FeatureStep({ user, onNext }: FeatureStepProps) {
	const onNextClick = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "feature",
			action: "next_step",
		});
		onNext();
	}, [onNext]);

	return (
		<>
			<Modal.Header noBottomBorder className={styles.header}>
				<h1>Hi {user.firstName}, welcome to your Team workspace!</h1>
			</Modal.Header>
			<Modal.Body className={styles.featureStep}>
				<h5>
					You and your teammates now have access to these features and more!{" "}
					<a
						href={ProductFamilies["PRO"].helpArticle}
						target={isDesktopApp || isTeams ? "_self" : "_blank"}
						rel="noopener"
					>
						Learn more about your new Team plan
					</a>
					.
				</h5>
				<section className={styles.contentBlock}>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<FontAwesomeIcon className={styles.icon} icon={faCommentAltLines} />
						</div>
						<p className={styles.highlightHeading}>Comments</p>
						<p className={styles.highlightText}>
							Easily provide updates, ask questions, and communicate with your team more efficiently with comments.
						</p>
					</div>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<MultiAssigneeIcon className={styles.svg} />
						</div>
						<p className={styles.highlightHeading}>Multiple assignees on action items</p>
						<p className={styles.highlightText}>
							Create action items with shared ownership or assign multiple individuals to complete the same task.
						</p>
					</div>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<FontAwesomeIcon className={styles.icon} icon={faBolt} />
						</div>
						<p className={styles.highlightHeading}>Meeting automations</p>
						<p className={styles.highlightText}>
							Turn on workflows to save you time, remind of upcoming meetings and circulate notes after the meeting has
							ended.
						</p>
					</div>
				</section>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.footer}>
				<Button variant="primary" onClick={onNextClick}>
					Continue
				</Button>
			</Modal.Footer>
		</>
	);
}

function AccessStep({ onNext }: StepProps) {
	const onNextClick = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "access",
			action: "next_step",
		});
		onNext();
	}, [onNext]);

	return (
		<>
			<Modal.Header noBottomBorder className={styles.header}>
				<h1>Access control</h1>
			</Modal.Header>
			<Modal.Body className={styles.accessStep}>
				<h5>Control who can join your workspace and enable collaboration with those outside your organization.</h5>
				<section className={styles.contentBlock}>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<FontAwesomeIcon className={styles.icon} icon={faSignIn} />
						</div>
						<p className={styles.highlightHeading}>Self sign up</p>
						<p className={styles.highlightText}>
							People with your organization&apos;s email domain can now sign up to your workspace. You can update this
							setting under Workspace settings &gt; Security.
						</p>
					</div>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<FontAwesomeIcon className={styles.icon} icon={faUserPlus} />
						</div>
						<p className={styles.highlightHeading}>Inviting users</p>
						<p className={styles.highlightText}>
							Members can invite people with your organization&apos;s email to join the workspace. You can update this
							setting under Workspace settings &gt; Security.
						</p>
					</div>
					<div className={styles.highlightBlock}>
						<div className={styles.highlightIcon}>
							<ConnectedIcon className={styles.svg} style={{ width: "28px", height: "28px" }} />
						</div>
						<p className={styles.highlightHeading}>External users</p>
						<p className={styles.highlightText}>
							Collaborate with people outsde of your organization in Fellow. You can share notes using their email
							address.
						</p>
					</div>
				</section>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.footer}>
				<Button variant="primary" onClick={onNextClick}>
					Continue
				</Button>
			</Modal.Footer>
		</>
	);
}

function InviteStep({ account, user, invites: suggestedInvites, onNext }: InviteStepProps) {
	const [invites, setInvites] = React.useState<InviteeType[]>([]);
	const [invitesSent, setInvitesSent] = React.useState<boolean>(false);
	const [invitesSuccess, setInvitesSuccess] = React.useState<boolean>(false);
	const [allEmpty, setAllEmpty] = React.useState<boolean>(false);

	const domains: string[] = (account.domains as Domain[]).reduce((acc: string[], domain: Domain) => {
		return domain ? [...acc, domain.domain, ...domain.aliases] : acc;
	}, []);
	const pattern = `^[^@]+@(${domains.map(escapeRegExp).join("|")})$`;
	const regex = domains.length > 0 ? new RegExp(pattern, "i") : new RegExp("^[^@]+@[^@]+\\.[a-zA-Z]+$");

	React.useEffect(() => {
		let propInvites = suggestedInvites.slice(0, 2);
		propInvites = new Array(4).fill(0).map((_item, i) => {
			return propInvites[i] ?? { id: uuidv4(), email: "" };
		});

		setInvites(propInvites);
	}, [suggestedInvites]);

	const onSkip = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "invite",
			action: "skip_invite",
		});
		onNext();
	}, [onNext]);

	const onInputChange = React.useCallback((id: string, email: string) => {
		setInvites(invites =>
			invites.map(invite => {
				if (invite.id === id) return { ...invite, error: "", email };
				return invite;
			}),
		);
	}, []);

	const areValid = React.useCallback((): boolean => {
		let valid = true;

		// Check for email format errors
		const emailRegex =
			domains.length > 0
				? new RegExp(`^[^@]+@(${domains.map(escapeRegExp).join("|")})$`, "i")
				: new RegExp("^[^@]+@[^@]+\\.[a-zA-Z]+$");

		setInvites(
			invites.map(invite => {
				const sameEmailAsUser = toLower(invite.email) === toLower(user.email);
				if ((!invite.email || emailRegex.test(invite.email)) && !sameEmailAsUser) return invite;

				valid = false;
				const error = sameEmailAsUser
					? `Must be an email different than ${user.email}`
					: domains.length > 0
						? `Must be a valid email ending in: ${listFormat(domains, { sep: ", ", last: " or " }).join("")}`
						: "Invalid email address";
				return { ...invite, error };
			}),
		);

		return valid;
	}, [domains, invites, user.email]);

	React.useEffect(() => {
		// Check if all invites in invites array are empty to prevent sending empty invites
		setAllEmpty(invites.every(invite => invite.email === ""));
	}, [invites]);

	const getInviteAnalytics = React.useCallback(() => {
		const invitesFromProps = suggestedInvites.filter(invite => invite.email);
		const invitesFromState = invites.filter(invite => invite.email);

		return {
			numInvites: invitesFromState.length,
			numInitialInvitesNotIncluded: invitesFromProps.filter(invite => invitesFromState.includes(invite)).length,
			numInitialInvites: invitesFromProps.length,
		};
	}, [suggestedInvites, invites]);

	const addTeammates = React.useCallback(async () => {
		if (!areValid()) return;
		if (invitesSent) return;

		setInvitesSent(true);

		const invitations: Invitation[] = invites
			.filter(invite => invite.email)
			.map(({ email }) => {
				return {
					email: email,
					fullName: nameFromEmailEntry(email),
					sendInviteEmail: true,
					relationship: user.isManager ? "SUBORDINATE" : "TEAMMATE",
				};
			});
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "invite",
			action: "invite_sent",
			feature: "post_upgrade_modal",
			...getInviteAnalytics(),
		});

		const captchaToken = await recaptcha("onboarding_invite");

		commitMutation<MutationType>(environment, {
			mutation: PostUpgradeModalContactMutation,
			variables: {
				input: {
					invitations,
					captchaToken,
					// eslint-disable-next-line camelcase
					analytics: JSON.stringify({ ux_location: "post_upgrade_modal__invite_step" }),
				},
			},
			onCompleted: ({ inviteUsers }, err) => {
				if (inviteUsers?.invitees) {
					const { invitees } = inviteUsers;
					const errors = invitees.map(invitee => invitee?.errors?.[0]).filter(error => error);

					if (err) {
						toast.error(`Something went wrong, and your invitations didn't go out! ${err}`);
						setInvitesSent(false);
					} else if (errors.length) {
						const error = errors[0];
						if (error) toast.error(`Something went wrong, and your invitations didn't go out! ${error.message}`);
						setInvitesSent(false);
					} else {
						setInvitesSuccess(true);
					}
				}
			},
		});
	}, [areValid, getInviteAnalytics, invites, invitesSent, user.isManager]);

	return (
		<>
			<Modal.Header noBottomBorder className={styles.header}>
				<h1>Add your teammates</h1>
			</Modal.Header>
			<Modal.Body>
				<h5>Fellow works better together. Invite your teammates to start having more effective meetings today.</h5>
				<div className={styles.inputGroup}>
					{invites.map(invite => (
						<InviteInput
							key={invite.id}
							invite={invite}
							domains={domains}
							regex={regex}
							onTextChange={onInputChange}
							className={styles.fullWidth}
						/>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.footer} style={{ paddingTop: 0 }}>
				{invitesSent ? (
					<p className={styles.inviteStatus}>
						{invitesSuccess ? (
							<>
								<FontAwesomeIcon icon={faCheck} /> Invites sent
							</>
						) : (
							<>
								<FontAwesomeIcon icon={faSpinnerThird} spin /> Inviting...
							</>
						)}
					</p>
				) : null}
				{invitesSent && invitesSuccess ? (
					<Button variant="primary" onClick={onNext}>
						Continue
					</Button>
				) : (
					<Button variant="primary" disabled={(invitesSent && !invitesSuccess) || allEmpty} onClick={addTeammates}>
						Add teammates
					</Button>
				)}
				<ButtonLink className={styles.skip} disabled={invitesSent && !invitesSuccess} onClick={onSkip}>
					Not now
				</ButtonLink>
			</Modal.Footer>
		</>
	);
}

function IntegrateStep({ appExtension, onNext }: IntegrateStepProps) {
	const { isDarkMode } = React.useContext(ThemeContext);

	const onSkip = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "integrate",
			action: "skip_integrate",
		});
		onNext();
	}, [onNext]);

	function ExtensionPrompt() {
		const extensionUri = withQueryParams(isFirefox ? mozillaStoreLink : chromeStoreLink, {
			utm_content: "post_upgrade_modal", // eslint-disable-line camelcase
		});

		const onAddToBrowser = React.useCallback(() => {
			window.open(extensionUri, "_blank", "noopener");
			dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
				step: "integrate",
				action: "download_extension",
			});
			onNext();
		}, [extensionUri]);

		return (
			<>
				<Modal.Header noBottomBorder className={styles.header}>
					<h1 style={{ textAlign: "center" }}>
						Add our <strong>{isFirefox ? "Firefox addon" : "Chrome extension"}</strong> for the best in&#8209;meeting
						experience in Google Meet
					</h1>
				</Modal.Header>
				<Modal.Body className={styles.integrateStep}>
					<section className={styles.cards}>
						<div className={styles.card}>
							<img src={isDarkMode ? companionIconDark : companionIcon} className={styles.cardIcon} />
							<div className={styles.cardTitle}>Notes in your video calls</div>
							<div className={styles.cardCopy}>
								Access your meeting notes directly from your video call or calendar event.
							</div>
						</div>
						<div className={styles.card}>
							<img src={isDarkMode ? trayIconDark : trayIcon} className={styles.cardIcon} />
							<div className={styles.cardTitle}>Tray menu</div>
							<div className={styles.cardCopy}>
								Quickly access your meetings, agendas, and action items from the URL bar.
							</div>
						</div>
					</section>
					<div className={styles.mockup}>
						<img src={isDarkMode ? extensionDark : extensionLight} />
					</div>
				</Modal.Body>
				<Modal.Footer noTopBorder className={styles.footer}>
					<Button variant="primary" onClick={onAddToBrowser}>
						<FontAwesomeIcon icon={isFirefox ? faFirefox : faChrome} style={{ marginRight: "0.5rem" }} />
						Add to {isFirefox ? "Firefox" : "Chrome"}
					</Button>
					<ButtonLink className={styles.skip} onClick={onSkip}>
						Not now
					</ButtonLink>
				</Modal.Footer>
			</>
		);
	}

	function DesktopPrompt() {
		const desktopUri = withQueryParams(desktopDownloadLink, {
			utm_content: "post_upgrade_modal", // eslint-disable-line camelcase
		});

		const onDownloadClick = React.useCallback(() => {
			window.open(desktopUri, "_blank", "noopener");
			dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
				step: "integrate",
				action: "download_desktop_app",
			});
			onNext();
		}, [desktopUri]);

		return (
			<>
				<Modal.Header noBottomBorder className={styles.header}>
					<h1 style={{ textAlign: "center" }}>
						Download our <strong>{isMacos ? "macOS" : "Windows"} app</strong> for the best Fellow experience
					</h1>
				</Modal.Header>
				<Modal.Body className={styles.integrateStep}>
					<section className={styles.cards}>
						<div className={styles.card}>
							<img src={isDarkMode ? companionIconDark : companionIcon} className={styles.cardIcon} />
							<div className={styles.cardTitle}>Companion mode</div>
							<div className={styles.cardCopy}>Bring your meeting agendas into your virtual calls automatically.</div>
						</div>
						<div className={styles.card}>
							<img src={isDarkMode ? notificationsIconDark : notificationsIcon} className={styles.cardIcon} />
							<div className={styles.cardTitle}>Meeting notifications</div>
							<div className={styles.cardCopy}>
								Jump straight into your video calls and Fellow notes with a single click.
							</div>
						</div>
					</section>
					<div className={styles.mockup}>
						<img src={isDarkMode ? desktopDark : desktopLight} />
					</div>
				</Modal.Body>
				<Modal.Footer noTopBorder className={styles.footer}>
					<Button variant="primary" onClick={onDownloadClick}>
						<FontAwesomeIcon icon={faArrowToBottom} style={{ marginRight: "0.5rem" }} />
						Download now
					</Button>
					<ButtonLink className={styles.skip} onClick={onSkip}>
						Not now
					</ButtonLink>
				</Modal.Footer>
			</>
		);
	}

	return appExtension === "extension" ? <ExtensionPrompt /> : <DesktopPrompt />;
}

function TrainingStep({ user, onNext }: TrainingStepProps) {
	const onBookTraining = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "training",
			action: "book_training",
		});
		const queryParams = {
			/* eslint-disable camelcase */
			first_name: user.firstName,
			last_name: user.lastName,
			email: user.email,
			/* eslint-enable camelcase */
		};
		bookTrainingCall(queryParams);
		onNext();
	}, [onNext, user.email, user.firstName, user.lastName]);

	const onSkip = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Action Taken", {
			step: "training",
			action: "skip_training",
		});
		onNext();
	}, [onNext]);

	return (
		<>
			<Modal.Header noBottomBorder className={styles.header}>
				<h1>Discovery session</h1>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.imageWrapper}>
					<img src={trainingImage} />
				</div>
				<h5>
					Get personalized guidance, review your new features, learn best practices, and ask any questions you may have
					in a discovery session with a Fellow meeting expert. It&apos;s included in your Team plan.
				</h5>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.footer}>
				<Button variant="primary" onClick={onBookTraining}>
					View session availability
				</Button>
				<ButtonLink className={styles.skip} onClick={onSkip}>
					I&apos;ll do this later
				</ButtonLink>
			</Modal.Footer>
		</>
	);
}

function FinalStep({ onNext }: StepProps) {
	return (
		<>
			<Animation useAnimation className={styles.animation} animationData={confettiAnimationData} />
			<Modal.Header noBottomBorder className={styles.header}>
				<h1>Congratulations! 🎉</h1>
			</Modal.Header>
			<Modal.Body>
				<h5>
					You&apos;re all set to start having more effective meetings with Fellow! Have more questions? We&apos;re here
					to help! <ContactSupportLink>Contact our team</ContactSupportLink>.
				</h5>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.footer}>
				<Button variant="primary" onClick={onNext}>
					Done
				</Button>
			</Modal.Footer>
		</>
	);
}

type Steps = "feature" | "access" | "invite" | "integrate" | "training" | "final";

export default function PostUpgradeModal({ onClose }: { onClose: () => void }) {
	const [step, setStep] = React.useState<Steps>("feature");
	const [journey, setJourney] = React.useState<Steps[]>([]);

	const { contacts, viewer } = useLazyLoadQuery<QueryType>(
		PostUpgradeModalQuery,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	const { dismiss } = React.useContext(DismissibleElementsContext);

	const invites = compact(contacts?.edges.map(edge => edge?.node))
		.filter(({ email }) => {
			return email && !emailGreylist.test(email);
		})
		.map(({ ...contact }) => ({
			id: uuidv4(),
			...contact,
		}));

	const { account, user, lastUsedBrowserExtension, lastUsedDesktopApp, integrations, questionAnswers } = viewer;
	const { primaryConferenceCallProvider: provider } = user.internalAttributes ?? {};

	const appExtension = React.useMemo(() => {
		/* Show the browser extension prompt if
		 * - the user has never used the extension
		 * - the main conference call provider is Google Meet
		 * - the user is authenticated with Google
		 * - the user is on a browser that supports the extension
		 */
		const showBrowserExtension =
			!lastUsedBrowserExtension &&
			window.CHROME_EXTENSION_ID &&
			provider === "HANGOUTS_MEET" &&
			!!integrations.google?.id &&
			(isChrome || isFirefox);

		// Show the desktop app prompt if the user has never used it
		const showDesktopApp = !lastUsedDesktopApp && (isWindows || isMacos);

		if (showBrowserExtension) return "extension";
		if (showDesktopApp) return "desktop";

		return null;
	}, [provider, integrations, lastUsedBrowserExtension, lastUsedDesktopApp]);

	const isEligilbleForTraining = React.useMemo(() => {
		return (
			(user.isManager ||
				questionAnswers?.is_manager ||
				user.internalAttributes?.isAccountCreator ||
				user.isAccountAdmin) &&
			account.billingAccount.numBillableUsers >= 9
		);
	}, [account, questionAnswers, user]);

	React.useEffect(() => {
		const journey: Steps[] = [];
		journey.push("feature");
		if (user.isAccountAdmin) journey.push("access");
		if (account.allowInviting && account.billingAccount.numBillableUsers < 5) journey.push("invite");
		if (appExtension && isDesktop) journey.push("integrate");
		if (isEligilbleForTraining) journey.push("training");
		journey.push("final");
		setJourney(journey);
	}, [
		account.allowInviting,
		account.billingAccount.numBillableUsers,
		appExtension,
		isEligilbleForTraining,
		user.isAccountAdmin,
	]);

	const onCloseClick = React.useCallback(() => {
		dispatchAnalyticsTrack("[Post Upgrade Modal] Dismissed", { step: step });
		onClose();
	}, [onClose, step]);

	const onNext = React.useCallback(() => {
		const nextStepIndex = journey.indexOf(step) + 1;
		if (nextStepIndex === journey.length) {
			dispatchAnalyticsTrack("[Post Upgrade Modal] Completed Journey");
			dismiss(POST_UPGRADE_MODAL_SEEN_KEY);
			onClose();
		} else {
			setStep(journey[nextStepIndex]);
		}
	}, [journey, step, dismiss, onClose]);

	function renderContent() {
		if (step === "feature") {
			return <FeatureStep user={user} onNext={onNext} />;
		} else if (step === "access") {
			return <AccessStep onNext={onNext} />;
		} else if (step === "invite") {
			return <InviteStep invites={invites} user={user} account={account} onNext={onNext} />;
		} else if (step === "integrate" && appExtension) {
			return <IntegrateStep appExtension={appExtension} onNext={onNext} />;
		} else if (step === "training") {
			return <TrainingStep user={user} onNext={onNext} />;
		} else if (step === "final") {
			return <FinalStep onNext={onNext} />;
		} else {
			return null;
		}
	}

	return (
		<Modal width="672px" className={styles.modal} onClose={onCloseClick}>
			{renderContent()}
		</Modal>
	);
}
